import { proxy } from "valtio";
import type {
	VoiceStageDurations as StageDurations,
	VoiceHeartbeatResponse,
	WallMicState,
} from "@/types/ai-assistant-voice";

/**
 * State tampilan mode suara panel asisten. Level modul (seperti
 * assistantStore): sesi tetap hidup saat panel ditutup sementara oleh penunjuk
 * Fitur 2 atau saat layout berganti karena navigasi.
 */

export type VoiceStatus =
	| "off"
	| "connecting"
	| "ready"
	| "listening"
	| "thinking"
	| "answering";

export type VoiceBeat = Pick<
	VoiceHeartbeatResponse,
	"elapsedSeconds" | "maxSeconds" | "remainingTodaySeconds"
>;

interface VoiceState {
	status: VoiceStatus;
	sessionId: string | null;
	/** Transkrip ucapan user yang sedang berjalan. */
	transcript: string;
	/** Laporan waktu terakhir dari server (heartbeat/start). */
	beat: VoiceBeat | null;
	/** `Date.now()` saat `beat` diterima — dasar hitung mundur lokal. */
	beatAt: number;
	/** Detak 1 detik untuk tampilan sisa waktu. */
	now: number;
	answerMuted: boolean;
	micMuted: boolean;
	/** Keadaan mikrofon Mode NOC di /wall (null = bukan Mode NOC). */
	wallMic: WallMicState | null;
	/** Tombol bicara Mode NOC sedang ditahan. */
	pttHeld: boolean;
	/** Pesan berakhir/galat terakhir (null = tidak ada). */
	notice: string | null;
	consentOpen: boolean;
	/** Transkrip kata pengisi GPT-Live giliran ini — baris abu-abu, bukan riwayat. */
	filler: string;
	/** Waktu per tahap per id pesan jawaban suara (memori tab saja, untuk ikon admin). */
	timings: Record<string, StageDurations>;
}

/** Jumlah giliran suara terakhir yang waktunya disimpan di memori tab. */
export const VOICE_TIMINGS_MAX = 50;

const idle = () => ({
	status: "off" as VoiceStatus,
	sessionId: null,
	transcript: "",
	beat: null,
	beatAt: 0,
	answerMuted: false,
	micMuted: false,
	wallMic: null,
	pttHeld: false,
	filler: "",
});

export const voiceStore = proxy<VoiceState>({
	...idle(),
	now: 0,
	notice: null,
	consentOpen: false,
	timings: {},
});

/** Kembalikan ke Off; `notice` menjelaskan kenapa (null = dimatikan user). */
export function resetVoice(notice: string | null) {
	Object.assign(voiceStore, idle(), { notice });
}

/** Simpan waktu tahap satu jawaban; entri tertua dibuang di atas `VOICE_TIMINGS_MAX`. */
export function recordVoiceTiming(
	messageId: string,
	durations: StageDurations,
) {
	const ids = Object.keys(voiceStore.timings).filter((id) => id !== messageId);
	const keep = ids.slice(Math.max(0, ids.length - VOICE_TIMINGS_MAX + 1));
	const next: Record<string, StageDurations> = {};
	for (const id of keep) {
		const entry = voiceStore.timings[id];
		if (entry) next[id] = entry;
	}
	next[messageId] = durations;
	voiceStore.timings = next;
}

export function isVoiceActive(): boolean {
	return voiceStore.status !== "off";
}
